import { Heart } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { createIntentKey, useFavoriteCommandQuery, useFavoriteMutation } from '../../api/index.js';
import { Button } from '../../design-system/button.js';
import { useWorkspace } from '../../app/runtime.js';

type FavoriteIntent = {
  commandId?: string;
  desired: boolean;
};

export function useFavoriteActionState({
  favorite,
  onSettled,
  trackId,
}: {
  favorite: boolean | null;
  onSettled?: () => void;
  trackId: string;
}) {
  const queryClient = useQueryClient();
  const mutation = useFavoriteMutation();
  const [intent, setIntent] = useState<FavoriteIntent>();
  const [providerFailure, setProviderFailure] = useState<unknown>();
  const settlingCommandId = useRef<string | undefined>(undefined);
  const command = useFavoriteCommandQuery(intent?.commandId);
  const displayedFavorite = intent?.desired ?? favorite === true;
  const pending =
    intent !== undefined ||
    mutation.isPending ||
    command.data?.status === 'queued' ||
    command.data?.status === 'applying';

  useEffect(() => {
    if (
      !intent?.commandId ||
      !command.data ||
      !['succeeded', 'failed', 'superseded'].includes(command.data.status) ||
      settlingCommandId.current === intent.commandId
    ) {
      return;
    }
    settlingCommandId.current = intent.commandId;
    if (command.data.status !== 'succeeded') {
      setProviderFailure(command.data.failure ?? new Error('Favorite update failed.'));
    }
    const settle = async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['catalog'] }),
        queryClient.invalidateQueries({ queryKey: ['library-tracks'] }),
        queryClient.invalidateQueries({ queryKey: ['track', trackId] }),
        queryClient.invalidateQueries({ queryKey: ['generation'] }),
        queryClient.invalidateQueries({ queryKey: ['activity'] }),
      ]);
      setIntent(undefined);
      onSettled?.();
    };
    void settle();
  }, [command.data, intent?.commandId, onSettled, queryClient, trackId]);

  const toggle = async () => {
    if (pending) return;
    const desired = !displayedFavorite;
    settlingCommandId.current = undefined;
    setProviderFailure(undefined);
    setIntent({ desired });
    try {
      const result = await mutation.mutateAsync({
        trackId,
        body: { favorite: desired },
        idempotencyKey: createIntentKey(),
      });
      setIntent((current) =>
        current?.desired === desired ? { commandId: result.id, desired } : current,
      );
    } catch (error) {
      setProviderFailure(error);
      setIntent(undefined);
    }
  };

  return {
    displayedFavorite,
    failure: providerFailure,
    pending,
    toggle,
  } as const;
}

export function FavoriteAction({
  favorite,
  trackId,
  compact = false,
}: {
  favorite: boolean | null;
  trackId: string;
  compact?: boolean;
}) {
  const { connection } = useWorkspace();
  const { displayedFavorite, failure, pending, toggle } = useFavoriteActionState({
    favorite,
    trackId,
  });

  return (
    <div className="grid justify-items-end">
      <Button
        aria-busy={pending || undefined}
        aria-disabled={pending || undefined}
        aria-label={displayedFavorite ? 'Unlike track' : 'Like track'}
        aria-pressed={displayedFavorite}
        className={
          displayedFavorite
            ? 'border-accent-line bg-accent-soft text-accent'
            : failure
              ? 'ring-1 ring-err'
              : undefined
        }
        disabled={connection.status !== 'active'}
        onClick={() => {
          if (!pending) void toggle();
        }}
        size={compact ? 'icon' : 'small'}
        title={failure ? 'Favorite update failed. Try again.' : undefined}
        variant="secondary"
      >
        <Heart aria-hidden="true" className={displayedFavorite ? 'fill-current' : undefined} />
        {compact ? null : displayedFavorite ? 'Liked' : 'Like'}
      </Button>
      {failure ? (
        <span className="sr-only" role="status">
          Favorite update failed.
        </span>
      ) : null}
    </div>
  );
}
